import styled, { css } from "styled-components";

import { IThemeProps } from "@/styles";
import { HEXtoRGBA } from "@/styles/helper";
import {
  backgroundFadeWithOverlay,
  fadeIn,
  rippleEffect,
  rippleEffectRuntime,
} from "@/styles/Mixins";

import { OptionEnum, OptionModeEnum, OptionTypeEnum } from "../index";

type ImageProps = IThemeProps<{
  imageUrl: string;
  answer?: OptionEnum;
  isClickable?: boolean;
}>;
export const Image = styled.div<ImageProps>`
  overflow: hidden;
  position: relative;
  width: 174px;
  height: 174px;
  background-image: url(${(props: ImageProps) =>
    `${props.imageUrl}?width=${174}&height=${174}`});
  @media ${(props: ImageProps) => props.theme.breakpoints.mobile} {
    width: 129px;
    height: 129px;
    background-image: url(${(props: ImageProps) =>
      `${props.imageUrl}?width=${129}&height=${129}`});
  }
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
`;

type ImageOverlayProps = IThemeProps<{
  opacity: number;
}>;
export const ImageOverlay = styled.div.attrs((props: ImageOverlayProps) => ({
  style: { opacity: props.opacity },
}))<ImageOverlayProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  font-size: ${(props: ImageOverlayProps) => props.theme.typography.h600};
  font-weight: 600;
  text-align: center;
  color: ${(props: ImageOverlayProps) => props.theme.colors.white};
  background-color: ${(props: ImageOverlayProps) =>
    HEXtoRGBA(props.theme.colors.black, 0.5)};
  z-index: ${(props: ImageOverlayProps) => props.theme.zIndex.h100};

  h1 {
    font-size: ${(props: ImageOverlayProps) => props.theme.typography.h900};
  }
`;

type TextBoxProps = IThemeProps<{
  show: boolean;
}>;
export const TextBox = styled.div<TextBoxProps>`
  visibility: ${(props: TextBoxProps) => (props.show ? "" : "hidden")};
  display: flex;
  height: ${(props: TextBoxProps) => (props.show ? "40px" : "0px")};
  width: 100%;
  position: absolute;
  bottom: 0;
  background-color: ${(props: TextBoxProps) => props.theme.colors.notification};
  color: ${(props: TextBoxProps) => props.theme.colors.white};
  align-items: center;
  justify-content: center;
  transition: height 0.15s ease-in-out;
  z-index: ${(props: TextBoxProps) => props.theme.zIndex.h100};
`;

type VotedBarProps = IThemeProps<{
  percent: string;
}>;
export const VotedBar = styled.div<VotedBarProps>`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: ${(props: VotedBarProps) => props.percent};
  background-color: ${(props: VotedBarProps) =>
    HEXtoRGBA(props.theme.colors.secondary, 0.6)};
  transition: height 1.5s linear;
`;

type PercentProps = IThemeProps<{
  answer?: OptionEnum;
}>;
export const Percent = styled.div<PercentProps>`
  position: relative;
  display: flex;
  height: 100%;
  font-size: ${(props: PercentProps) =>
    props.answer ? props.theme.typography.h900 : props.theme.typography.h800};
  font-weight: bold;
  color: ${(props: PercentProps) => props.theme.colors.white};
  align-items: center;
  justify-content: center;
  z-index: ${(props: PercentProps) => props.theme.zIndex.h300};

  @media ${(props: PercentProps) => props.theme.breakpoints.mobile} {
    font-size: ${(props: PercentProps) =>
      props.answer ? props.theme.typography.h800 : props.theme.typography.h600};
  }
`;

type StatusProps = IThemeProps<{
  type: OptionTypeEnum;
  answer?: OptionEnum;
  overlay?: boolean;
  withoutRipple?: boolean;
}>;

export const Status = styled.div<StatusProps>`
  display: flex;
  height: 100%;
  text-align: center;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  ${(props: StatusProps) => {
    const {
      theme: { colors },
      type,
      answer,
      overlay,
      withoutRipple,
    } = props;
    if (type === OptionTypeEnum.quiz) {
      const color =
        answer === OptionEnum.correct ? colors.success : colors.error;
      return withoutRipple
        ? backgroundFadeWithOverlay({
            opacity: 0,
            background: HEXtoRGBA(color, 0.9),
            timing: 800,
            animationDelay: 300,
          })
        : rippleEffectRuntime({
            opacity: 1,
            background: HEXtoRGBA(color, 0.9),
            timing: 800,
          });
    } else if (type === OptionTypeEnum.poll) {
      return rippleEffect({
        opacity: 0.5,
        overlay: overlay,
        background:
          answer === OptionEnum.vote ? colors.secondary100 : colors.black,
      });
    } else {
      return "";
    }
  }}
`;

export const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  svg {
    margin-top: -10px;
  }
  @media ${(props: IThemeProps) => props.theme.breakpoints.mobile} {
    svg {
      margin-top: 0;
    }
  }
`;

type StatusTextProps = IThemeProps<{
  lite?: boolean;
}>;
export const StatusText = styled.p<StatusTextProps>`
  position: absolute;
  bottom: ${(props: StatusTextProps) => (props.lite ? 10 : 15)}px;
  left: 0;
  width: 100%;
  text-align: center;
  color: ${(props: StatusTextProps) => props.theme.colors.white};
  font-size: ${(props: StatusTextProps) =>
    props.theme.typography[props.lite ? "h400" : "h700"]};
  z-index: ${(props: StatusTextProps) => props.theme.zIndex.h300};
  font-weight: 600;

  @media ${(props: StatusTextProps) => props.theme.breakpoints.mobile} {
    font-size: ${(props: StatusTextProps) =>
      props.theme.typography[props.lite ? "h200" : "h400"]};
  }
`;

type FadeInProps = IThemeProps<{
  timing?: number;
  delay?: number;
}>;
export const FadeIn = styled.div<FadeInProps>`
  opacity: 0;
  animation: ${fadeIn} ${(props: FadeInProps) => props.timing || 1000}ms
    ease-out forwards;
  animation-delay: ${(props: FadeInProps) => props.delay || 1000}ms;
`;

type MetaProps = IThemeProps<{
  invert: boolean;
}>;
export const Meta = styled.div<MetaProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: ${(props: MetaProps) =>
    props.invert
      ? props.theme.colors.votingVoteText
      : props.theme.colors.votingOptionText};
  font-size: ${(props: MetaProps) => props.theme.typography.h300};
  line-height: ${(props: MetaProps) => props.theme.typography.h400};
  padding: 5px;
  min-height: 65px;

  a {
    color: ${(props: MetaProps) =>
      props.invert ? props.theme.colors.white : props.theme.colors.black};
  }
`;

export const Name = styled.h1`
  font-family: ${(props: IThemeProps) => props.theme.globals.fontFamily};
  font-size: ${(props: IThemeProps) => props.theme.typography.h410};
  line-height: ${(props: IThemeProps) => props.theme.typography.h500};
  padding-bottom: 5px;
`;

export const Description = styled.div`
  * {
    padding: 3px;
  }

  h1 {
    font-size: ${(props: IThemeProps) => props.theme.typography.h400};
    line-height: ${(props: IThemeProps) => props.theme.typography.h500};
  }

  a,
  p {
    font-size: ${(props: IThemeProps) => props.theme.typography.h200};
    line-height: 1.4;
    color: inherit;
    text-decoration: underline;
  }
`;

type ImageWrapperProps = IThemeProps<{
  mode: OptionModeEnum;
  type: OptionTypeEnum;
  answer?: OptionEnum;
  isClickable: boolean;
  bottomRadius?: boolean;
}>;

export const ImageWrapper = styled.div<ImageWrapperProps>(
  (props: ImageWrapperProps) => {
    const {
      theme: { breakpoints, colors, shadows, globals, borders },
      mode,
      type,
      answer,
      isClickable,
      bottomRadius,
    } = props;

    const bgColor =
      answer === OptionEnum.vote ? colors.secondary : colors.white;
    const cursor =
      !isClickable || mode === OptionModeEnum.results ? "default" : "pointer";

    const notInFocus = type !== OptionTypeEnum.poll && !isClickable && !answer;

    return css`
    width: 182px;
    height: 182px;
    @media ${breakpoints.mobile} {
        width: 137px;
        height: 137px;
    }
    overflow: hidden;
    display: inline-flex;
    flex-direction: column;
    cursor: ${cursor};
    background-color: ${bgColor};
    border-color: ${bgColor};
    border-style: solid;
    border-width: ${borders.imageTile};
    box-shadow: ${shadows.s200};
    transition: all 0.25s ease-in;
    ${bottomRadius && `border-radius: 0 0 5px 5px;`}
    ${notInFocus && `opacity: 0.5;`}
    & > ${Description} {
        ${answer === OptionEnum.vote &&
          `color: ${colors.white};

            a {
                color: ${colors.white};
            }
        `}
    }

    ${!globals.isTouch &&
      answer !== OptionEnum.vote &&
      isClickable &&
      `
        &:hover {
        border-color: ${isClickable ? colors.secondary : colors.white};
        border-width: ${borders.imageTileHover};

        & > ${Description} {
            color: ${isClickable ? colors.secondary : colors.white};
            a {
                color: inherit;
            }
        }
    }`}
`;
  }
);

type ImageWrapperExtendedProps = IThemeProps<{
  mode: OptionModeEnum;
  type: OptionTypeEnum;
  answer?: OptionEnum;
  isClickable: boolean;
  bottomRadius?: boolean;
}>;

export const ImageWrapperExtended = styled.div<ImageWrapperExtendedProps>(
  ({
    theme: { breakpoints, colors, shadows, globals },
    mode,
    type,
    answer,
    isClickable,
    bottomRadius,
  }: ImageWrapperExtendedProps) => css`
    position: relative;
    width: 186px;
    @media ${breakpoints.mobile} {
      width: 141px;
    }
    overflow: hidden;
    display: inline-flex;
    flex-direction: column;
    cursor: ${!isClickable || mode === OptionModeEnum.results
      ? "default"
      : "pointer"};
    background-color: ${answer === OptionEnum.vote
      ? colors.votingVoteColor
      : colors.votingOptionColor};
    border-color: ${colors.white};
    border-style: solid;
    border-width: 2px;
    padding: 4px;
    box-shadow: ${shadows.s200};
    transition: all 0.25s ease-in;
    ${bottomRadius && "border-radius: 8px;"}
    ${Image} {
      border-radius: 4px 4px 0 0;
    }
    ${type !== OptionTypeEnum.poll &&
      !isClickable &&
      !answer &&
      `opacity: 0.5;`}

    ${!globals.isTouch &&
      answer !== OptionEnum.vote &&
      isClickable &&
      `
        &:hover {
            ${Meta} {
                color: ${colors.votingOptionTextHover};
            }
    }`}
  `
);

export const Bubble = styled.div<IThemeProps>`
  position: absolute;
  top: 8px;
  right: 8px;
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${(props: IThemeProps) => props.theme.globals.fontFamily};
  font-size: ${(props: IThemeProps) => props.theme.typography.h410};
  font-weight: 600;
  background-color: ${(props: IThemeProps) =>
    props.theme.colors.votingPercentBubbleColor};
  border-radius: 50%;
  color: ${(props: IThemeProps) => props.theme.colors.votingPercentBubbleText};
  opacity: 0;
  animation: ${fadeIn} 0.2s ease-in;
  animation-fill-mode: forwards;
`;
